import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Close } from "@mui/icons-material";
import moment from "moment";
import "moment/locale/id";
import { textPrimary } from "../../constants/colors";
import { ParamsGetDetailShift } from "../../constants/types";
import useDetailShift from "../../services/queries/useDetailShift";
import useUkm from "../../services/queries/useUkm";
import useUser from "../../services/queries/useUser";
import { getLocalUkmId } from "../../utils/handleChangeUkmId";
import toRupiah from "../../utils/toRupiah";

interface IModalDetailShiftProps {
    fullScreen: boolean;
    isOpenModalDetailShift: boolean;
    closeModalDetailShift: () => void;
    shiftId: number;
}

function hexToRgb(hex: string | undefined): string {
    if (!hex) return "255, 255, 255";

    hex = hex.replace(/^#/, "");

    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;

    return `${r}, ${g}, ${b}`;
}

const ModalDetailShift = ({
    fullScreen,
    isOpenModalDetailShift,
    closeModalDetailShift,
    shiftId,
}: IModalDetailShiftProps) => {
    const { data: user } = useUser();

    const initialParamsUkm = {
        size: 500,
        page: 1,
        search: "",
    };
    const { data: ukm } = useUkm(initialParamsUkm);
    const ukmId = React.useMemo(() => {
        if (user?.userId) {
            return getLocalUkmId(user?.userId);
        }
        return null;
    }, [user?.userId]);

    const ukmData = React.useMemo(() => {
        if (ukmId) {
            return ukm?.content.find((li) => li.id === ukmId);
        }
        return ukm?.content[0];
    }, [ukm?.content, ukmId]);

    const initialParamsGetDetailShift = {
        shiftId: shiftId,
    };

    const [paramsListDetailShift, setParamsDetailShift] =
        useState<ParamsGetDetailShift>(initialParamsGetDetailShift);

    const { data: detailShift, refetch: refetchDetailShift } = useDetailShift(
        paramsListDetailShift,
        {
            enabled: false,
        },
    );

    useEffect(() => {
        if (isOpenModalDetailShift && shiftId) {
            setParamsDetailShift((prev) => ({
                ...prev,
                shiftId: shiftId,
            }));
            refetchDetailShift();
        }
    }, [isOpenModalDetailShift, shiftId, refetchDetailShift]);

    return (
        <Dialog
            maxWidth="md"
            fullScreen={fullScreen}
            fullWidth={true}
            open={isOpenModalDetailShift}
            scroll="body"
            onClose={() => {
                closeModalDetailShift();
            }}
            PaperProps={{
                sx: {
                    borderRadius: 1,
                },
            }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                >
                    <Typography fontSize={32} fontWeight="bold">
                        Detail Shift
                    </Typography>
                    <IconButton
                        onClick={() => {
                            closeModalDetailShift();
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Box
                    padding={2}
                    borderRadius={4}
                    sx={{
                        backgroundColor: `rgba(${hexToRgb(ukmData?.warna)}, 0.2)`,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography color={textPrimary.body}>
                                Kasir
                            </Typography>

                            <Typography fontSize={18} fontWeight="600">
                                {detailShift?.kasir.nama}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography color={textPrimary.body}>
                                Tanggal
                            </Typography>

                            <Typography fontSize={18} fontWeight="600">
                                {moment(detailShift?.mulaiShift).format(
                                    "DD MMMM YYYY",
                                )}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography color={textPrimary.body}>
                                Shift Mulai
                            </Typography>

                            <Typography fontSize={18} fontWeight="600">
                                {moment(detailShift?.mulaiShift).format(
                                    "DD MMMM YYYY HH:mm",
                                )}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography color={textPrimary.body}>
                                Shift Berakhir
                            </Typography>

                            <Typography fontSize={18} fontWeight="600">
                                {moment(detailShift?.akhirShift).format(
                                    "DD MMMM YYYY HH:mm",
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Stack padding={2} gap={2} mt={2} mb={2}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontSize={16} variant="subtitle2">
                                Kas awal
                            </Typography>
                        </Stack>
                        <Typography fontSize={18}>
                            {toRupiah(detailShift?.kasAwal ?? 0)}
                        </Typography>
                    </Box>

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontSize={16} variant="subtitle2">
                                Pendapatan
                            </Typography>
                        </Stack>
                        <Typography fontSize={18}>
                            {toRupiah(detailShift?.pendapatan ?? 0)}
                        </Typography>
                    </Box>

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontSize={16} variant="subtitle2">
                                Pengeluaran
                            </Typography>
                        </Stack>
                        <Typography fontSize={18}>
                            {toRupiah(detailShift?.pengeluaran ?? 0)}
                        </Typography>
                    </Box>
                </Stack>

                <Stack
                    padding={2}
                    borderRadius={4}
                    sx={{
                        backgroundColor: `rgba(${hexToRgb(ukmData?.warna)}, 0.2)`,
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontSize={16} variant="subtitle2">
                                Penerimaan Sistem
                            </Typography>
                        </Stack>
                        <Typography fontSize={18}>
                            {toRupiah(detailShift?.penerimaanSistem ?? 0)}
                        </Typography>
                    </Box>
                </Stack>

                <Stack
                    padding={2}
                    borderRadius={4}
                    mt={3}
                    sx={{
                        backgroundColor: `rgba(${hexToRgb(ukmData?.warna)}, 0.2)`,
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontSize={16} variant="subtitle2">
                                Penerimaan Aktual
                            </Typography>
                        </Stack>
                        <Typography fontSize={18}>
                            {toRupiah(detailShift?.kasAkhir ?? 0)}
                        </Typography>
                    </Box>
                </Stack>

                <Stack
                    padding={2}
                    borderRadius={4}
                    mt={3}
                    sx={{
                        backgroundColor: `rgba(${hexToRgb(ukmData?.warna)}, 0.2)`,
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontSize={16} variant="subtitle2">
                                Selisih
                            </Typography>
                        </Stack>
                        <Typography fontSize={18}>
                            {toRupiah(detailShift?.selisih ?? 0)}
                        </Typography>
                    </Box>
                </Stack>

                <Box mt={2}>
                    <Typography fontSize={19} fontWeight={500}>
                        Catatan
                    </Typography>

                    <Typography fontSize={18}>
                        {detailShift?.catatan}
                    </Typography>
                </Box>

                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    gap={1}
                    mt={3}
                    flex={1}
                >
                    <Button
                        variant="contained"
                        onClick={() => {
                            closeModalDetailShift();
                        }}
                    >
                        Kembali
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ModalDetailShift;
